import axios from 'axios';
import type { Product, PriceHistoryEntry, CheckResult, Settings, Stats } from '../types';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
      window.dispatchEvent(new CustomEvent('session-expired'));
    }
    return Promise.reject(error);
  }
);

// Products
export const getProducts = async (): Promise<Product[]> => {
  const { data } = await api.get<Product[]>('/products');
  return data;
};

export const getProduct = async (id: number): Promise<Product> => {
  const { data } = await api.get<Product>(`/products/${id}`);
  return data;
};

export const createProduct = async (product: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<Product> => {
  const { data } = await api.post<Product>('/products', product);
  return data;
};

export const updateProduct = async (id: number, product: Partial<Product>): Promise<Product> => {
  const { data } = await api.put<Product>(`/products/${id}`, product);
  return data;
};

export const deleteProduct = async (id: number): Promise<void> => {
  await api.delete(`/products/${id}`);
};

// Prices
export const getPriceHistory = async (productId: number, days = 90): Promise<PriceHistoryEntry[]> => {
  const { data } = await api.get<PriceHistoryEntry[]>(`/products/${productId}/prices?days=${days}`);
  return data;
};

export const checkPrice = async (productId: number): Promise<CheckResult> => {
  const { data } = await api.post<CheckResult>(`/products/${productId}/check-price`);
  return data;
};

export const checkAllPrices = async (): Promise<CheckResult[]> => {
  const { data } = await api.post<CheckResult[]>('/products/check-all');
  return data;
};

// Settings
export const getSettings = async (): Promise<Settings> => {
  const { data } = await api.get<Settings>('/settings');
  return data;
};

export const updateSettings = async (settings: Partial<Settings>): Promise<Settings> => {
  const { data } = await api.put<Settings>('/settings', settings);
  return data;
};

export const testEmail = async (): Promise<{ success: boolean; message: string }> => {
  const { data } = await api.post<{ success: boolean; message: string }>('/settings/test-email');
  return data;
};

export const testTelegram = async (): Promise<{ success: boolean; message: string }> => {
  const { data } = await api.post<{ success: boolean; message: string }>('/settings/test-telegram');
  return data;
};

export const testNtfy = async (): Promise<{ success: boolean; message: string }> => {
  const { data } = await api.post<{ success: boolean; message: string }>('/settings/test-ntfy');
  return data;
};

// Stats
export const getStats = async (): Promise<Stats> => {
  const { data } = await api.get<Stats>('/stats');
  return data;
};

// Reports
export const getReport = async (): Promise<string> => {
  const { data } = await api.get<string>('/report', { responseType: 'text' });
  return data;
};

// Auth
export const checkAuth = async (): Promise<{ authenticated: boolean; authRequired: boolean }> => {
  const { data } = await api.get<{ authenticated: boolean; authRequired: boolean }>('/auth/check');
  return data;
};

export const login = async (password: string): Promise<{ success: boolean }> => {
  const { data } = await api.post<{ success: boolean }>('/auth/login', { password });
  return data;
};

export const logout = async (): Promise<{ success: boolean }> => {
  const { data } = await api.post<{ success: boolean }>('/auth/logout');
  return data;
};